/**
 * LLM 管理仪表盘页面
 *
 * 显示 LLM 使用统计和管理设置，包括：
 * - 概览统计卡片
 * - 池统计表格
 * - 模型统计表格
 * - 趋势图表
 * - 统计仪表盘 (新)
 * - 设置管理 (新组件)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AdminLayout } from '../components/layout/AdminLayout';
import { llmAdminApi, type OverviewStats, type PoolStats, type ModelStats, type TrendData, type HealthCheckResult, type UserDistributionItem, type ModelHealthResponse, type ModelHealthItem } from '../services/llmAdminApi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar } from 'recharts';
import { AvailableModelsDashboard } from '../components/llm/AvailableModelsDashboard';
import { SettingsEditor } from '../components/llm/SettingsEditor';
import { StatsDashboard } from '../components/llm/StatsDashboard';
import { QuotaManager } from '../components/llm/QuotaManager';
import { AlertConfig } from '../components/llm/AlertConfig';

/**
 * 格式化数字显示
 */
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

/**
 * 格式化百分比显示
 */
const formatPercent = (num: number): string => {
  return `${(num * 100).toFixed(1)}%`;
};

/**
 * 统计卡片组件
 */
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  color?: 'blue' | 'green' | 'yellow' | 'red';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };

  return (
    <div className={`p-4 rounded-lg border ${colorClasses[color]}`}>
      <div className="text-sm font-medium opacity-80">{title}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {subtitle && <div className="text-xs mt-1 opacity-60">{subtitle}</div>}
    </div>
  );
};

/**
 * 池统计表格组件
 */
interface PoolStatsTableProps {
  stats: PoolStats[];
  healthData: ModelHealthResponse | null;
  loading: boolean;
}

const PoolStatsTable: React.FC<PoolStatsTableProps> = ({ stats, healthData, loading }) => {
  const getPoolHealthStatus = (providerName: string, poolIndex: number): { available: number; unavailable: number } => {
    const poolModels = healthData?.models.filter(m => m.provider_name === providerName && m.pool_index === poolIndex) || [];
    return {
      available: poolModels.filter(m => m.available).length,
      unavailable: poolModels.filter(m => !m.available).length,
    };
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>;
  }

  if (stats.length === 0) {
    return <div className="text-center py-8 text-gray-500">暂无数据</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left p-3 font-medium text-gray-600">提供商 / 池</th>
            <th className="text-left p-3 font-medium text-gray-600">健康状态</th>
            <th className="text-right p-3 font-medium text-gray-600">请求数</th>
            <th className="text-right p-3 font-medium text-gray-600">成功率</th>
            <th className="text-right p-3 font-medium text-gray-600">Token</th>
            <th className="text-right p-3 font-medium text-gray-600">平均延迟</th>
            <th className="text-left p-3 font-medium text-gray-600">模型</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((stat, index) => {
            const poolHealth = getPoolHealthStatus(stat.provider_name, stat.pool_index);
            return (
              <tr key={`${stat.provider_name}-${stat.pool_index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="p-3 font-medium">
                  {stat.provider_name} / #{stat.pool_index}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {poolHealth.available > 0 && (
                      <span className="text-xs text-green-600">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block mr-1" />
                        {poolHealth.available} 可用
                      </span>
                    )}
                    {poolHealth.unavailable > 0 && (
                      <span className="text-xs text-red-600">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block mr-1" />
                        {poolHealth.unavailable} 不可用
                      </span>
                    )}
                    {!healthData && (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </div>
                </td>
                <td className="text-right p-3">
                  {formatNumber(stat.usage_stats.total_requests)}
                </td>
                <td className="text-right p-3">
                  <span className={stat.usage_stats.error_rate < 0.1 ? 'text-green-600' : 'text-red-600'}>
                    {formatPercent(1 - stat.usage_stats.error_rate)}
                  </span>
                </td>
                <td className="text-right p-3">
                  {formatNumber(stat.usage_stats.total_tokens)}
                </td>
                <td className="text-right p-3">
                  {stat.usage_stats.avg_latency_ms ? `${Math.round(stat.usage_stats.avg_latency_ms)}ms` : '-'}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {stat.models.slice(0, 3).map((model) => (
                      <span key={model} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        {model}
                      </span>
                    ))}
                    {stat.models.length > 3 && (
                      <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                        +{stat.models.length - 3}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * 模型统计表格组件
 */
interface ModelStatsTableProps {
  stats: ModelStats[];
  healthData: ModelHealthResponse | null;
  loading: boolean;
}

const ModelStatsTable: React.FC<ModelStatsTableProps> = ({ stats, healthData, loading }) => {
  const getHealthStatus = (modelUse: string): ModelHealthItem | undefined => {
    return healthData?.models.find(m => m.model_use === modelUse);
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>;
  }

  if (stats.length === 0) {
    return <div className="text-center py-8 text-gray-500">暂无数据</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left p-3 font-medium text-gray-600">模型</th>
            <th className="text-left p-3 font-medium text-gray-600">提供商</th>
            <th className="text-left p-3 font-medium text-gray-600">健康状态</th>
            <th className="text-right p-3 font-medium text-gray-600">请求数</th>
            <th className="text-right p-3 font-medium text-gray-600">成功率</th>
            <th className="text-right p-3 font-medium text-gray-600">Token</th>
            <th className="text-right p-3 font-medium text-gray-600">池数量</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((stat, index) => {
            const health = getHealthStatus(stat.model_use);
            return (
              <tr key={stat.model_use} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="p-3 font-medium">{stat.model_use}</td>
                <td className="p-3 text-gray-500">{stat.provider_name || '-'}</td>
                <td className="p-3">
                  {health ? (
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          health.available ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        title={health.available ? '可用' : '不可用'}
                      />
                      <span className={`text-xs ${health.available ? 'text-green-600' : 'text-red-600'}`}>
                        {health.available ? '可用' : '不可用'}
                      </span>
                      {!health.available && health.error_count > 0 && (
                        <span className="text-xs text-red-500">({health.error_count} 错误)</span>
                      )}
                      {health.latency_ms > 0 && (
                        <span className="text-xs text-gray-500">{health.latency_ms}ms</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">-</span>
                  )}
                </td>
                <td className="text-right p-3">{formatNumber(stat.usage_stats.total_requests)}</td>
                <td className="text-right p-3">
                  <span className={stat.usage_stats.error_rate < 0.1 ? 'text-green-600' : 'text-red-600'}>
                    {formatPercent(1 - stat.usage_stats.error_rate)}
                  </span>
                </td>
                <td className="text-right p-3">{formatNumber(stat.usage_stats.total_tokens)}</td>
                <td className="text-right p-3">{stat.pool_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * 用户分布表格组件
 */
interface UserDistributionTableProps {
  stats: UserDistributionItem[];
  loading: boolean;
}

const UserDistributionTable: React.FC<UserDistributionTableProps> = ({ stats, loading }) => {
  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>;
  }

  if (stats.length === 0) {
    return <div className="text-center py-8 text-gray-500">暂无数据</div>;
  }

  // 计算总请求数用于百分比计算
  const totalRequests = stats.reduce((sum, item) => sum + item.request_count, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-center p-3 font-medium text-gray-600 w-12">排名</th>
            <th className="text-left p-3 font-medium text-gray-600">用户</th>
            <th className="text-right p-3 font-medium text-gray-600">请求数</th>
            <th className="text-right p-3 font-medium text-gray-600">占比</th>
            <th className="text-right p-3 font-medium text-gray-600">Token</th>
            <th className="text-right p-3 font-medium text-gray-600">平均延迟</th>
            <th className="text-right p-3 font-medium text-gray-600">错误数</th>
          </tr>
        </thead>
        <tbody>
          {stats.map((stat, index) => {
            const percentage = totalRequests > 0 ? (stat.request_count / totalRequests) : 0;
            const rank = index + 1;
            const rankColor = rank === 1 ? 'text-yellow-500' : rank === 2 ? 'text-gray-400' : rank === 3 ? 'text-orange-400' : 'text-gray-500';
            
            return (
              <tr key={stat.user_id || `user-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="text-center p-3">
                  <span className={`font-bold ${rankColor}`}>
                    {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                  </span>
                </td>
                <td className="p-3 font-medium">
                  {stat.nickname || stat.username || stat.user_id?.substring(0, 8) || '未知用户'}
                </td>
                <td className="text-right p-3">{formatNumber(stat.request_count)}</td>
                <td className="text-right p-3">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${percentage * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500">{formatPercent(percentage)}</span>
                  </div>
                </td>
                <td className="text-right p-3">{formatNumber(stat.total_tokens)}</td>
                <td className="text-right p-3">
                  {stat.avg_latency_ms ? `${Math.round(stat.avg_latency_ms)}ms` : '-'}
                </td>
                <td className="text-right p-3">
                  <span className={stat.error_count > 0 ? 'text-red-600' : 'text-gray-400'}>
                    {stat.error_count}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * 趋势图表组件
 */
interface TrendChartsProps {
  data: TrendData | null;
  loading: boolean;
}

const TrendCharts: React.FC<TrendChartsProps> = ({ data, loading }) => {
  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>;
  }

  if (!data || (!data.requests?.length && !data.tokens?.length)) {
    return <div className="text-center py-8 text-gray-500">暂无趋势数据</div>;
  }

  // 格式化时间戳显示
  const formatTimestamp = (ts: string) => {
    try {
      const date = new Date(ts);
      return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
    } catch {
      return ts;
    }
  };

  // 准备图表数据
  const chartData = data.requests?.map((req, idx) => ({
    time: formatTimestamp(req.timestamp),
    requests: req.value,
    tokens: data.tokens?.[idx]?.value || 0,
    errors: data.errors?.[idx]?.value || 0,
    latency: data.latency?.[idx]?.value || 0,
  })) || [];

  return (
    <div className="space-y-6">
      {/* 请求趋势图 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-600 mb-4">请求趋势</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 4 }}
              />
              <Area 
                type="monotone" 
                dataKey="requests" 
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.2}
                name="请求数"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Token 趋势图 */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-4">Token 使用趋势</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ fontSize: 12, borderRadius: 4 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="tokens" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  dot={false}
                  name="Token"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 错误趋势图 */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-600 mb-4">错误趋势</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" />
                <Tooltip 
                  contentStyle={{ fontSize: 12, borderRadius: 4 }}
                />
                <Bar 
                  dataKey="errors" 
                  fill="#ef4444" 
                  radius={[2, 2, 0, 0]}
                  name="错误数"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 延迟趋势图 */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-600 mb-4">平均延迟趋势</h3>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9ca3af" unit="ms" />
              <Tooltip 
                contentStyle={{ fontSize: 12, borderRadius: 4 }}
              />
              <Line 
                type="monotone" 
                dataKey="latency" 
                stroke="#f59e0b" 
                strokeWidth={2}
                dot={false}
                name="延迟"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

/**
 * 实时监控组件
 */
interface LiveMonitoringFeedProps {
  data: Record<string, unknown> | null;
}

interface RequestLog {
  request_id: string;
  timestamp: string;
  model_use: string;
  provider: string;
  pool_index: number;
  model_name: string;
  user_id?: string;
  status: 'pending' | 'completed' | 'failed';
  latency_ms?: number;
  error?: string;
}

interface QueueData {
  size: number;
  processed: number;
}

const LiveMonitoringFeed: React.FC<LiveMonitoringFeedProps> = ({ data }) => {
  const [isPaused, setIsPaused] = useState(false);

  const formatTime = (ts: string) => {
    try {
      const date = new Date(ts);
      return date.toLocaleTimeString();
    } catch {
      return ts;
    }
  };

  const formatDuration = (ms: number | undefined) => {
    if (ms === undefined || ms === null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const queues = data?.queues as Record<string, QueueData> | undefined;
  const queueEntries = queues ? Object.entries(queues) : [];
  const recentLogs = (data?.recent_logs as RequestLog[]) || [];

  return (
    <div className="space-y-4">
      {/* 系统状态概览 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">动态路由</div>
          <div className="text-lg font-bold mt-1">
            {data?.dynamic_routing_enabled ? (
              <span className="text-green-600">已启用</span>
            ) : (
              <span className="text-gray-400">未启用</span>
            )}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">活跃队列</div>
          <div className="text-lg font-bold mt-1">{queueEntries.length}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">最后更新</div>
          <div className="text-lg font-bold mt-1">
            {data?.timestamp ? formatTime(data.timestamp as string) : '-'}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="text-sm text-gray-500">队列状态</div>
          <div className="text-lg font-bold mt-1">
            {data?.error ? (
              <span className="text-red-600">错误</span>
            ) : (
              <span className="text-green-600">正常</span>
            )}
          </div>
        </div>
      </div>

      {/* 队列详情 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="font-medium text-gray-800">队列状态</h3>
          <button
            onClick={() => setIsPaused(!isPaused)}
            className={`px-3 py-1 rounded text-sm ${
              isPaused ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
            }`}
          >
            {isPaused ? '继续' : '暂停'}
          </button>
        </div>

        {queueEntries.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {queueEntries.map(([key, value]) => {
              const queueData = value as QueueData;
              return (
                <div key={key} className="p-4 flex justify-between items-center">
                  <div>
                    <span className="font-medium text-gray-700">{key}</span>
                    <span className="ml-2 text-sm text-gray-500">
                      {queueData?.size || 0} 请求等待中
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${Math.min((queueData?.size || 0) / 10 * 100, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm text-gray-500">
                      {queueData?.processed || 0} 已处理
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            {data?.dynamic_routing_enabled
              ? '当前没有活跃的请求队列'
              : '动态路由未启用，无法显示实时监控'}
          </div>
        )}
      </div>

      {/* 实时请求日志 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-800">实时请求日志</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {recentLogs.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {recentLogs.map((log) => {
                const statusColor = log.status === 'completed'
                  ? 'text-green-600 bg-green-50'
                  : log.status === 'failed'
                    ? 'text-red-600 bg-red-50'
                    : 'text-yellow-600 bg-yellow-50';

                return (
                  <div key={log.request_id} className="p-3 text-sm hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusColor}`}>
                          {log.status === 'completed' ? '成功' : log.status === 'failed' ? '失败' : '进行中'}
                        </span>
                        <span className="font-medium text-gray-700">{log.model_name}</span>
                        <span className="text-gray-400">via</span>
                        <span className="text-gray-600">{log.provider}</span>
                        <span className="text-gray-400">#{log.pool_index}</span>
                      </div>
                      <div className="flex items-center gap-3 text-gray-500">
                        {log.latency_ms !== undefined && log.latency_ms !== null && (
                          <span className="text-xs">{formatDuration(log.latency_ms)}</span>
                        )}
                        <span className="text-xs">{formatTime(log.timestamp)}</span>
                      </div>
                    </div>
                    {log.error && (
                      <div className="mt-1 text-xs text-red-600 truncate" title={log.error}>
                        {log.error}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-gray-400">
                      {log.request_id}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              等待请求...
              <div className="text-xs text-gray-400 mt-2">
                请求将在运行时显示
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 监控说明 */}
      <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
        <strong>实时监控说明：</strong>
        显示队列状态和最近20条请求日志。
        <div className="mt-2 text-xs text-blue-600">
          刷新间隔：5秒 | 显示最近20条请求
        </div>
      </div>
    </div>
  );
};

type TabType = 'overview' | 'trends' | 'statistics' | 'monitoring' | 'settings' | 'quota' | 'alerts';

const TABS: { key: TabType; label: string }[] = [
  { key: 'overview', label: '概览' },
  { key: 'trends', label: '趋势分析' },
  { key: 'statistics', label: '统计仪表盘' },
  { key: 'monitoring', label: '实时监控' },
  { key: 'settings', label: '设置管理' },
  { key: 'quota', label: '配额管理' },
  { key: 'alerts', label: '告警配置' },
];

/**
 * LLM 管理仪表盘页面组件
 */
export const LLMAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = useMemo<TabType>(() => {
    const tab = searchParams.get('tab') as TabType;
    return TABS.some(t => t.key === tab) ? tab : 'overview';
  }, [searchParams]);

  const handleTabChange = useCallback((tab: TabType) => {
    setSearchParams(prev => {
      prev.set('tab', tab);
      return prev;
    });
  }, [setSearchParams]);

  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null);
  const [poolStats, setPoolStats] = useState<PoolStats[]>([]);
  const [modelStats, setModelStats] = useState<ModelStats[]>([]);
  const [userStats, setUserStats] = useState<UserDistributionItem[]>([]);
  const [trendData, setTrendData] = useState<TrendData | null>(null);
  const [liveData, setLiveData] = useState<Record<string, unknown> | null>(null);
  const [healthData, setHealthData] = useState<ModelHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(false);
  const [healthResult, setHealthResult] = useState<HealthCheckResult | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [overview, pools, models, users, trends, health] = await Promise.all([
        llmAdminApi.getOverviewStats(),
        llmAdminApi.getPoolStats(),
        llmAdminApi.getModelStats(),
        llmAdminApi.getUserStats(),
        llmAdminApi.getTrends(undefined, undefined, undefined, 'hour'),
        llmAdminApi.getModelHealth(),
      ]);

      setOverviewStats(overview);
      setPoolStats(pools);
      setModelStats(models);
      setUserStats(users);
      setTrendData(trends);
      setHealthData(health);
      setLastUpdate(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取统计数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab !== 'overview') return;

    fetchStats();

    if (!autoRefresh) return;
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats, autoRefresh, activeTab]);

  // 实时监控数据刷新（每5秒）
  useEffect(() => {
    if (activeTab !== 'monitoring') return;

    const fetchLive = async () => {
      try {
        const data = await llmAdminApi.getLiveMonitoring();
        setLiveData(data);
      } catch (err) {
        console.error('Failed to fetch live monitoring data:', err);
      }
    };

    fetchLive();
    const interval = setInterval(fetchLive, 5000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // 触发健康检查
  const handleValidate = async () => {
    setValidating(true);
    setHealthResult(null);

    try {
      const result = await llmAdminApi.validateSettings();
      setHealthResult(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '健康检查失败');
    } finally {
      setValidating(false);
    }
  };

  // 导出 CSV
  const handleExportCsv = async () => {
    try {
      const blob = await llmAdminApi.exportCsv(undefined, 'pool');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `llm-stats-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    }
  };

  // 导出 JSON
  const handleExportJson = async () => {
    try {
      const data = await llmAdminApi.exportJson();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `llm-stats-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : '导出失败');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">LLM 管理仪表盘</h1>
            {lastUpdate && (
              <p className="text-sm text-gray-500 mt-1">
                最后更新: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
              自动刷新
            </label>
            <button
              onClick={handleValidate}
              disabled={validating}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {validating && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              健康检查
            </button>
            <button
              onClick={handleExportCsv}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              导出 CSV
            </button>
            <button
              onClick={handleExportJson}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              导出 JSON
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError('')} className="ml-4 text-red-500 hover:text-red-700">
              关闭
            </button>
          </div>
        )}

        {healthResult && (
          <div className={`mb-6 p-4 rounded-lg border ${
            healthResult.success_rate >= 0.8 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : healthResult.success_rate >= 0.5 
                ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="font-medium mb-2">健康检查结果</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="opacity-60">密钥验证:</span>
                <span className="ml-2 font-medium">{healthResult.valid_keys}/{healthResult.total_keys}</span>
              </div>
              <div>
                <span className="opacity-60">模型验证:</span>
                <span className="ml-2 font-medium">{healthResult.valid_models}/{healthResult.total_models}</span>
              </div>
              <div>
                <span className="opacity-60">成功率:</span>
                <span className="ml-2 font-medium">{formatPercent(healthResult.success_rate)}</span>
              </div>
              <div>
                <span className="opacity-60">告警:</span>
                <span className="ml-2 font-medium">{healthResult.alert_triggered ? '是' : '否'}</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-1 mb-6 border-b border-gray-200">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-white border border-b-white border-gray-200 text-blue-600 -mb-px'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            <AvailableModelsDashboard />
            
            {overviewStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                <StatCard
                  title="今日请求"
                  value={formatNumber(overviewStats.total_requests_today)}
                  color="blue"
                />
                <StatCard
                  title="今日 Token"
                  value={formatNumber(overviewStats.total_tokens_today)}
                  color="green"
                />
                <StatCard
                  title="今日活跃用户"
                  value={overviewStats.active_users_today}
                  color="blue"
                />
                <StatCard
                  title="今日错误率"
                  value={formatPercent(overviewStats.error_rate_today)}
                  color={overviewStats.error_rate_today < 0.05 ? 'green' : 'red'}
                />
                <StatCard
                  title="提供商数量"
                  value={overviewStats.total_providers}
                  subtitle="活跃"
                />
                <StatCard
                  title="池数量"
                  value={overviewStats.total_pools}
                  subtitle="总计"
                />
                <StatCard
                  title="模型数量"
                  value={overviewStats.total_models}
                  subtitle="已配置"
                />
              </div>
            )}

            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-800">池统计</h2>
              </div>
              <PoolStatsTable stats={poolStats} healthData={healthData} loading={loading} />
            </div>

            <div className="bg-white rounded-lg border border-gray-200 mb-6">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-800">模型统计</h2>
              </div>
              <ModelStatsTable stats={modelStats} healthData={healthData} loading={loading} />
            </div>

            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-800">用户分布 (Top 20)</h2>
              </div>
              <UserDistributionTable stats={userStats} loading={loading} />
            </div>
          </>
        )}

        {activeTab === 'trends' && (
          <TrendCharts data={trendData} loading={loading} />
        )}

        {activeTab === 'statistics' && (
          <StatsDashboard autoRefresh={autoRefresh} refreshInterval={30000} />
        )}

        {activeTab === 'monitoring' && (
          <LiveMonitoringFeed data={liveData} />
        )}

        {activeTab === 'settings' && (
          <SettingsEditor />
        )}

        {activeTab === 'quota' && (
          <QuotaManager autoRefresh={autoRefresh} refreshInterval={30000} />
        )}

        {activeTab === 'alerts' && (
          <AlertConfig />
        )}

        <div className="mt-4 text-center text-sm text-gray-500">
          <button onClick={fetchStats} className="text-blue-500 hover:text-blue-700">
            立即刷新
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LLMAdminPage;
